import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  AccountDeletionCounts,
  AccountDeletionDetail,
  AccountDeletionFilters,
  AccountDeletionListResponse,
  AccountDeletionRequest,
} from './types';

export async function fetch_deletion_list(
  filters: AccountDeletionFilters,
  sort?: SortState,
): Promise<AccountDeletionListResponse> {
  const res = await api.get<AccountDeletionListResponse>('/admin/account-deletions', {
    params: build_params(filters as Record<string, never>, sort),
  });
  return res.data;
}

/** The tiles. Describes the whole queue, so it deliberately ignores the page's filters. */
export async function fetch_deletion_counts(): Promise<AccountDeletionCounts> {
  const res = await api.get<AccountDeletionCounts>('/admin/account-deletions/counts');
  return res.data;
}

export async function fetch_deletion_detail(id: string): Promise<AccountDeletionDetail> {
  const res = await api.get<AccountDeletionDetail>(`/admin/account-deletions/${id}`);
  return res.data;
}

export async function export_deletions(
  filters: AccountDeletionFilters,
  sort?: SortState,
): Promise<void> {
  await download_export(
    '/admin/account-deletions/export',
    build_params(filters as Record<string, never>, sort),
    'account-deletions.csv',
  );
}

/** Grant it: the account is deactivated now, and erased after the grace window. */
export async function approve_deletion(
  id: string,
  note?: string,
): Promise<AccountDeletionRequest> {
  const res = await api.post<AccountDeletionRequest>(
    `/admin/account-deletions/${id}/approve`,
    note ? { note } : {},
  );
  return res.data;
}

/** Refuse it. The reason is shown to the requester word for word. */
export async function reject_deletion(id: string, reason: string): Promise<AccountDeletionRequest> {
  const res = await api.post<AccountDeletionRequest>(`/admin/account-deletions/${id}/reject`, {
    reason,
  });
  return res.data;
}

/**
 * Withdraw on the account holder's behalf, restoring the account.
 *
 * Reaches further than the app's own cancel: during the grace window the account is
 * deactivated, so the person it belongs to cannot call anything themselves. This is the
 * "contact support to cancel" path the public page promises.
 */
export async function cancel_deletion(id: string, reason: string): Promise<AccountDeletionRequest> {
  const res = await api.post<AccountDeletionRequest>(`/admin/account-deletions/${id}/cancel`, {
    reason,
  });
  return res.data;
}
